import React from 'react';
import './AdminPage.css';
import { getNews, saveNews, resetNews, getScadenze, saveScadenze, resetScadenze, ensureNewsLoaded, ensureScadenzeLoaded } from '../data';
import { logout } from '../auth';
import RichEditor from '../components/admin/RichEditor';
import { clearPendingUploadsForDatasets, collectUploadFiles, createPendingUploadsState, hasUnsavedDraftChanges, sanitizeSlug, upsertPendingUpload, validateImageUpload } from './adminPageUploadState';

// Static GitHub config (edit here once)
// Set these values to your repo and Netlify hook
const GIT_CFG = {
  owner: 'alessandrosca2704',
  repo: 'sito-studio',
  branch: 'main',
  userName: 'Site Admin',
  userEmail: 'admin@example.com',
  deployHook: '' // optional Netlify build hook URL
};

const DATASETS = {
  news: {
    label: 'News',
    singular: 'news',
    basePath: '/news',
    get: getNews,
    save: saveNews,
    reset: resetNews,
    exportName: (lang) => `news_${lang}.json`,
    commitPath: 'public/assets/news.json',
    commitPaths: {
      it: 'public/assets/news.it.json',
      en: 'public/assets/news.en.json',
      legacy: 'public/assets/news.json'
    },
    commitMessage: 'chore: update news via /admin',
    buildPayload: () => ({ it: getNews('it'), en: getNews('en') }),
    buildLocalizedPayload: (payload) => ({
      it: { items: payload.it || [] },
      en: { items: payload.en || [] }
    })
  },
  deadlines: {
    label: 'Scadenze',
    singular: 'scadenza',
    basePath: '/scadenze',
    get: getScadenze,
    save: saveScadenze,
    reset: resetScadenze,
    exportName: (lang) => `scadenze_${lang}.json`,
    commitPath: 'public/assets/scadenze.json',
    commitPaths: {
      it: 'public/assets/scadenze.it.json',
      en: 'public/assets/scadenze.en.json',
      legacy: 'public/assets/scadenze.json'
    },
    commitMessage: 'chore: update scadenze via /admin',
    buildPayload: () => ({ it: getScadenze('it'), en: getScadenze('en') }),
    buildLocalizedPayload: (payload) => ({
      it: { items: payload.it || [] },
      en: { items: payload.en || [] }
    })
  }
};

const DATASET_OPTIONS = [
  { value: 'news', label: DATASETS.news.label },
  { value: 'deadlines', label: DATASETS.deadlines.label }
];

const MULTI_COMMIT_MESSAGE = 'chore: update news & scadenze via /admin';

function emptyPost(){
  return { slug:'', title:'', excerpt:'', image:'', content:'' };
}

export default function AdminPage(){
  const lang = (typeof window!=='undefined' && document.documentElement.lang) || 'it';
  const [dataset, setDataset] = React.useState('news');
  const [items, setItems] = React.useState(()=>DATASETS.news.get(lang));
  const [current, setCurrent] = React.useState(emptyPost());
  const [editingIndex, setEditingIndex] = React.useState(-1);
  const [uploadInfo, setUploadInfo] = React.useState(null); // { base64, ext }
  const [pendingUploads, setPendingUploads] = React.useState(createPendingUploadsState);
  const pendingUploadsRef = React.useRef(pendingUploads);
  const [commitStatus, setCommitStatus] = React.useState('');
  const datasetCfg = DATASETS[dataset];
  const isNews = dataset === 'news';
  const formTitle = editingIndex === -1 ? (isNews ? 'Nuovo Articolo' : 'Nuova Scadenza') : (isNews ? "Modifica un'articolo" : 'Modifica una scadenza');
  const imageFolder = isNews ? 'news' : 'scadenze';
  const pendingDraft = hasUnsavedDraftChanges(uploadInfo, current, items, editingIndex);
  const hasPendingUpload = Boolean(uploadInfo);
  const statusBadgeText = hasPendingUpload ? 'Upload pronto' : pendingDraft ? 'Draft non salvato' : 'Pronto';
  const statusBadgeTone = hasPendingUpload ? 'warning' : pendingDraft ? 'danger' : 'success';

  React.useEffect(() => {
    pendingUploadsRef.current = pendingUploads;
  }, [pendingUploads]);

  React.useEffect(()=>{
    let mounted = true;
    const cfg = DATASETS[dataset];
    const ensureFn = dataset === 'news' ? ensureNewsLoaded : ensureScadenzeLoaded;
    const ensurePromise = ensureFn ? ensureFn() : Promise.resolve();
    Promise.resolve(ensurePromise)
      .catch(()=>{})
      .finally(()=>{
        if (!mounted) return;
        setItems(cfg.get(lang));
        setEditingIndex(-1);
        setCurrent(emptyPost());
        setUploadInfo(null);
      });
    return () => { mounted = false; };
  }, [lang, dataset]);

  const confirmPendingChangesBeforeChange = (actionLabel) => {
    if (!hasUnsavedDraftChanges(uploadInfo, current, items, editingIndex)) return true;
    return window.confirm(`Hai modifiche non salvate. Vuoi davvero ${actionLabel} senza salvarle?`);
  };
  const startNew = (skipGuard = false) => {
    if (!skipGuard && !confirmPendingChangesBeforeChange('creare un nuovo elemento senza salvare')) return;
    setCurrent(emptyPost());
    setEditingIndex(-1);
    setUploadInfo(null);
  };
  const editItem = (i) => {
    if (!confirmPendingChangesBeforeChange('cambiare articolo senza salvare')) return;
    setEditingIndex(i);
    setCurrent(items[i]);
    setUploadInfo(null);
  };
  const updateField = (k,v) => setCurrent(prev => ({...prev, [k]: v}));
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setCurrent(prev => ({
      ...prev,
      title,
      slug: sanitizeSlug(title)
    }));
  };
  const getImagePath = (slugValue, ext) => `/assets/${imageFolder}/${slugValue}${ext}`;

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateImageUpload(file);
    if (validationError) {
      alert(validationError);
      e.target.value = '';
      return;
    }
    const slugValue = (current.slug || '').trim();
    if (!slugValue) { alert('Per caricare un immagine inserisci prima lo slug'); e.target.value = ''; return; }
    const matchExt = file.name.match(/\.[a-zA-Z0-9]+$/);
    const ext = matchExt ? matchExt[0].toLowerCase() : '.jpg';
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = reader.result || '';
        const base64 = typeof result === 'string' ? result.split(',').pop() : '';
        if (!base64) throw new Error('File non valido');
        const imgPath = getImagePath(slugValue, ext);
        setCurrent(prev => ({ ...prev, image: imgPath }));
        setUploadInfo({ base64, ext });
      } catch (err){
        alert('Errore durante la lettura del file immagine');
      }
    };
    reader.readAsDataURL(file);
  };

  const pushPendingUpload = (slugValue, ext) => {
    if (!uploadInfo) return null;
    const imgPath = getImagePath(slugValue, ext);
    const repoPath = imgPath.startsWith('/') ? `public${imgPath}` : `public/${imgPath}`;
    const nextState = upsertPendingUpload(pendingUploadsRef.current, dataset, repoPath, uploadInfo.base64);
    pendingUploadsRef.current = nextState;
    setPendingUploads(nextState);
    setUploadInfo(null);
    return imgPath;
  };

  const save = () => {
    const slugValue = sanitizeSlug(current.title);
    if (!current.title || !slugValue) { alert('Title and slug are required'); return null; }
    let next = { ...current, slug: slugValue };
    if (uploadInfo) {
      const imgPath = pushPendingUpload(slugValue, uploadInfo.ext);
      if (imgPath) { next.image = imgPath; }
    }
    const arr = [...items];
    const existsIdx = arr.findIndex(p => p.slug === slugValue);
    if (editingIndex === -1) {
      if (existsIdx !== -1) { alert('Slug already exists'); return null; }
      arr.unshift(next);
    } else {
      if (existsIdx !== -1 && existsIdx !== editingIndex) { alert('Slug already exists'); return null; }
      arr[editingIndex] = next;
    }
    setItems(arr);
    datasetCfg.save(lang, arr);
    startNew(true);
    return arr;
  };

  const remove = (i) => {
    if (!window.confirm(`Delete this ${datasetCfg.singular}?`)) return;
    const arr = items.filter((_,idx)=> idx!==i);
    setItems(arr);
    datasetCfg.save(lang, arr);
    startNew();
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(items, null, 2)], {type: 'application/json'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = datasetCfg.exportName(lang);
    a.click();
  };

  const importJson = (e) => {
    const file = e.target.files?.[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const arr = JSON.parse(reader.result);
        if (!Array.isArray(arr)) throw new Error('Invalid JSON array');
        setItems(arr);
        datasetCfg.save(lang, arr);
        startNew();
      } catch(err){ alert('Invalid JSON file'); }
    };
    reader.readAsText(file);
  };

  const reset = () => {
    if (!window.confirm(`Reset ${datasetCfg.label.toLowerCase()} to defaults?`)) return;
    datasetCfg.reset(lang);
    setItems(datasetCfg.get(lang));
    startNew();
  };

  const describeTargets = (keys) => {
    if (keys.length === 1) return DATASETS[keys[0]].label;
    return 'News + Scadenze';
  };

  const performCommit = async (files, message) => {
    if (!files.length) throw new Error('No files to commit');
    const response = await fetch('/.netlify/functions/adminPublish', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files, message })
    });
    if (response.status === 401) { await logout(); window.location.href = '/login'; throw new Error('Session expired'); }
    if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(data.error || `Publish failed (${response.status})`); }
  };

  const commitDatasets = async (targetKeys) => {
    const keys = Array.from(new Set((targetKeys || []).filter(Boolean)));
    if (!keys.length) { setCommitStatus('No datasets selected'); return; }

    const shouldPersistCurrentDataset = keys.includes(dataset);
    const hasDraft = shouldPersistCurrentDataset && hasUnsavedDraftChanges(uploadInfo, current, items, editingIndex);
    let savedItems = items;
    if (hasDraft) {
      const shouldSave = window.confirm('Hai modifiche non salvate nel form corrente. Vuoi salvarle prima di fare il commit?');
      if (shouldSave) {
        savedItems = save() || items;
      } else {
        setCommitStatus('Commit annullato: ci sono modifiche non salvate nel form.');
        return;
      }
    }

    if (shouldPersistCurrentDataset) {
      datasetCfg.save(lang, savedItems);
    }

    const label = describeTargets(keys);
    setCommitStatus(`Preparing commit (${label})...`);

    try {
      const files = [];
      keys.forEach((key) => {
        const cfg = DATASETS[key];
        if (!cfg) throw new Error(`Unknown dataset ${key}`);
        const payload = cfg.buildPayload();
        const localized = cfg.buildLocalizedPayload(payload);
        files.push({ path: cfg.commitPaths.it, content: JSON.stringify(localized.it, null, 2) });
        files.push({ path: cfg.commitPaths.en, content: JSON.stringify(localized.en, null, 2) });
        files.push({ path: cfg.commitPaths.legacy, content: JSON.stringify(payload, null, 2) });
      });

      files.push(...collectUploadFiles(pendingUploadsRef.current, {}, keys));

      const message = keys.length === 1 ? DATASETS[keys[0]].commitMessage : MULTI_COMMIT_MESSAGE;
      await performCommit(files, message);
      setCommitStatus(`Commit done (${label}).`);
      // clear pending uploads for committed datasets
      const clearedState = clearPendingUploadsForDatasets(pendingUploadsRef.current, keys);
      pendingUploadsRef.current = clearedState;
      setPendingUploads(clearedState);

      if (GIT_CFG.deployHook){
        setCommitStatus(`Commit OK (${label}). Triggering Netlify...`);
        try {
          await fetch(GIT_CFG.deployHook, { method:'POST', mode:'no-cors' });
          setCommitStatus(`Deploy hook sent (${label}).`);
        } catch {
          setCommitStatus(`Commit OK (${label}). Deploy hook unreachable.`);
        }
      }
    } catch(err){
      setCommitStatus(`Commit error: ${err.message || String(err)}`);
    }
  };

  return (
    <section className="section admin">
      <div className="container admin__topbar">
            <div style={{display:'flex', alignItems:'center', gap:12}}>
              <h2 style={{margin:0}}>Admin</h2>
              <select
                value={dataset}
                onChange={(e) => {
                  if (!confirmPendingChangesBeforeChange('cambiare dataset senza salvare')) return;
                  setDataset(e.target.value);
                }}
                className="admin__dataset-select"
              >
                {DATASET_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
        <a className="btn btn-brand admin__cms-link" href="/admin/CMS/">CMS</a>
        <button className="btn" onClick={async()=>{ await logout(); window.location.href = '/login'; }}>Logout</button>
      </div>
      <div className="container admin__layout">
        <div className="admin__list">
          <div className="admin__listhead">
              <h2 style={{margin:0}}>{datasetCfg.label} ({lang.toUpperCase()})</h2>

            <div className="admin__actions">
              <button className="btn" onClick={() => startNew()}>New</button>
              <button className="btn" onClick={exportJson}>Export</button>
              <label className="btn" style={{cursor:'pointer'}}>
                Import
                <input type="file" accept="application/json" onChange={importJson} style={{display:'none'}}/>
              </label>
              <button className="btn" onClick={reset}>Reset</button>
            </div>
          </div>
          <ul className="admin__items">
            {items.map((p,i)=> (
              <li key={p.slug} className={i===editingIndex? 'active':''}>
                <div className="title" onClick={()=>editItem(i)}>{p.title}</div>
                <div className="slug">{`${datasetCfg.basePath}/${p.slug}`}</div>
                <button className="link danger" onClick={()=>remove(i)}>Delete</button>
              </li>
            ))}
          </ul>
        </div>
        <div className="admin__form">
          <h3>{formTitle}</h3>
          <div className="admin__status-row full">
            <span className={`admin__status-badge admin__status-badge--${statusBadgeTone}`}>{statusBadgeText}</span>
            {pendingDraft && <span className="admin__status-help">Salva il record prima del commit per pubblicare correttamente il contenuto.</span>}
          </div>
          <div className="form-admin">

            <label>
              <span>Titolo</span>
              <input value={current.title} onChange={handleTitleChange} />
            </label>
            <label>
              <span>Slug (url)</span>
              <input value={current.slug} readOnly />
            </label>
            <label className="full">
              <span>Immagine (upload)</span>
              <input type="file" accept="image/*" onChange={handleImageUpload} />
              {current.image && <div className="help">File: {current.image}</div>}
            </label>
            <label className="full">
              <span>Estratto</span>
              <textarea rows={3} value={current.excerpt} onChange={e=>updateField('excerpt', e.target.value)} />
            </label>
            <label className="full editor">
              <span>Contenuto</span>
              <RichEditor value={current.content} onChange={(val)=>updateField('content', val)} />
            </label>
          </div>
          <div className="admin__formactions">
            <button className="btn btn-brand" onClick={save}>Save</button>
          </div>
        </div>
      </div>

      <div className="container panel">
        <div className="panel__head">
          <h3>GitHub Commit (Netlify deploy)</h3>
        </div>
        <div className="cfg-grid">
          <div className="cfg-readonly">
            <div><strong>Repo:</strong> {GIT_CFG.owner}/{GIT_CFG.repo}</div>
            <div><strong>Branch:</strong> {GIT_CFG.branch}</div>
            <div><strong>File news:</strong> {DATASETS.news.commitPath}</div>
            <div><strong>File scadenze:</strong> {DATASETS.deadlines.commitPath}</div>
          </div>
          <p className="full">Le credenziali GitHub sono conservate esclusivamente nelle variabili protette di Netlify.</p>
        </div>
        <div className="panel__actions">
          
          <button className="btn" onClick={()=>commitDatasets(['news'])}>Commit news.json</button>
          <button className="btn" onClick={()=>commitDatasets(['deadlines'])}>Commit scadenze.json</button>
          <button className="btn btn-brand" onClick={()=>commitDatasets(['news','deadlines'])}>Commit news + scadenze</button>
          <div className="status">{commitStatus}</div>
        </div>
      </div>
    </section>
  );
}
